import date from 'quasar/src/utils/date/date.js';import extend from 'quasar/src/utils/extend/extend.js';
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, reactive, ref, toRefs, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { api, ShomeiEntry } from "~/api";
import BreadcrumbList from "~/components/BreadcrumbList.vue";
import { MsgBox } from "~/components/MsgBox";
import { Log } from "~/extensions/utils";
import { createAddress } from "~/extensions/utils/AddressHelper";
import { PageID, ShomeiSb, userType } from "~/extensions/utils/DefineTypes";
import { commonStore } from "~/store/CommonStore";
import { myPageStore } from "~/store/MyPageStore";
import { useLoad } from "./composition/Load";
import { ShomeiEntryValidator } from "./Models/ShomeiEntryValidator";
import { ShomeiEntryViewModel } from "./Models/ShomeiEntryViewModel";
import { store } from "./store/ShomeiEntryStore";
const log = Log.create("ShomeiEntryPage");
export default defineComponent({
    name: "ShomeiEntryPage",
    components: {
        BreadcrumbList
    },
    beforeRouteEnter(to, from, next) {
        const fromScreen = from.name;
        const relatedScreens = ["shomei-precautions", "shomei-shinsei-entry-check"];
        if (store.privacyConsent && relatedScreens.includes(fromScreen)) {
            next();
            return;
        }
        next({ name: "shomei-room-list" });
    },
    props: {
        updateValue: {
            type: Function,
            required: true
        },
        model: {
            type: String,
            required: true
        },
        isPickMonth: {
            type: Boolean,
            default: true
        },
        canDatePicker: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        isFirstOnly: {
            type: Boolean,
            default: false
        }
    },
    async setup(props) {
        const route = useRoute();
        // ぱんくずリスト用
        const isMultipleKeiyaku = myPageStore.isMultipleKeiyaku;
        // 画面名取得
        const pageName = commonStore.pageName(PageID.shomeiShinsei);
        let shomeiNo = route.params.shomeiNo;
        const addressAll = myPageStore.selectedNyukyokeiyaku?.addressAll;
        const { load, loading } = useLoad(shomeiNo);
        const personal = reactive(myPageStore.personal);
        const shomei = reactive(new ShomeiEntry());
        const shomeiEntryViewModel = new ShomeiEntryViewModel(shomei);
        const valid = ref(new ShomeiEntryValidator(shomeiEntryViewModel));
        let html = reactive({
            value: ""
        });
        let shosikiSiteiHtml = reactive({
            value: ""
        });
        const router = useRouter();
        const qInputProxy = ref(null);
        const startDateInput = ref(store.shomeiEntry.rentStartDate);
        const endDateInput = ref(store.shomeiEntry.rentEndDate);
        const qStartPopupProxy = ref(null);
        const qEndPopupProxy = ref(null);
        const qStartDateProxy = ref(null);
        const qEndDateProxy = ref(null);
        const defaultViewType = ref(props.isPickMonth ? "Months" : "Calendar");
        const dateMaskFormat = ref(props.isPickMonth ? "YYYY/MM" : "YYYY/MM/DD");
        const isMonthToYear = ref(false);
        const isMonthMoveYear = ref(false);
        const isNotClickDay = ref(false);
        const options = computed(() => (props.isFirstOnly ? firstOnly : allDay));
        /** 年月日入力桁数 */
        const dateDigits = props.isPickMonth ? 6 : 8;
        /** IME入力中フラグ */
        let isComposing = false;
        /** IME入力中のテキスト */
        let composingInputted = "";
        // ファイルアップロード
        const fileInput = ref(null);
        // 初期表示処理
        const initView = async () => {
            try {
                html.value = await api.shomei.loadEntryText();
                shosikiSiteiHtml.value = await api.shomei.loadShosikiSiteiText();
            }
            catch (ex) {
                log.error("setup", ex);
                MsgBox.error(ex?.message ?? "エラーが発生しました。");
                router.push({ name: "top" });
            }
        };
        const firstOnly = (date) => {
            return date.endsWith("/01");
        };
        const allDay = (date) => {
            return true;
        };
        //住所検索
        let findAddress = async () => {
            if (valid.value.data.error && valid.value.data.errorFields.includes("postcode")) {
                return;
            }
            //ハイフン対応
            if (shomei.postcode.length === 7) {
                shomei.postcode = shomei.postcode.substring(0, 3) + "-" + shomei.postcode.substring(3);
            }
            try {
                if (/^\d{3}-\d{4}$/.test(shomei.postcode)) {
                    shomei.adr1 = await createAddress(shomei.postcode);
                }
            }
            catch (ex) {
                // 該当する住所が存在しない場合は404が投げられる
                if (ex?.status !== 404) {
                    log.error("findAddress", ex);
                }
            }
        };
        // ハイフン付きの電話番号に整形
        let formatTel = () => {
            if (myPageStore.personal.userKb === userType.hojin) {
                return;
            }
            if (valid.value.data.error && valid.value.data.errorFields.includes("tel")) {
                return;
            }
            //ハイフン対応
            if (myPageStore.personal.userKb === userType.Kojin && shomei.tel.length !== 13 && shomei.tel.length !== 0) {
                let temp = shomei.tel.replace("-", "");
                shomei.tel = temp.substring(0, 3) + "-" + temp.substring(3, 7) + "-" + temp.substring(7);
            }
        };
        // 証明書選択
        let onChanged = function (data) {
            let selectedShako = data.selectedShomeiKbn?.hanyoCd === ShomeiSb.Shako;
            let selectedKyoju = data.selectedShomeiKbn?.hanyoCd === ShomeiSb.Kyoju;
            let selectedRent = data.selectedShomeiKbn?.hanyoCd === ShomeiSb.Chinryo;
            // 車庫情報
            if (selectedShako) {
                data.shosikiSiteiKb = 0;
            }
            data.shakoChushamei = "";
            data.shakoKukaku = "";
            data.shinseishaKb = 1;
            data.shinseishaName = "";
            data.shinseishaPostcode = "";
            data.shinseishaAdr1 = "";
            data.shinseishaAdr2 = "";
            data.shinseishaAdr3 = "";
            data.shinseishaTel = "";
            //居住証明書
            data.shogakukinKb = 1;
            //賃料支払証明期間
            data.rentStartDate = "";
            data.rentEndDate = "";
            startDateInput.value = null;
            endDateInput.value = null;
            data.isShakoShomei = selectedShako;
            data.isKyojuShomei = selectedKyoju;
            data.isRentShomei = selectedRent;
        };
        // 契約者が申請する時、項目をクリアする
        const clearFields = (val) => {
            if (val === 1) {
                shomei.shinseishaName = "";
                shomei.shinseishaPostcode = "";
                shomei.shinseishaAdr1 = "";
                shomei.shinseishaAdr2 = "";
                shomei.shinseishaAdr3 = "";
                shomei.shinseishaTel = "";
            }
        };
        //申請者住所検索
        let findShinseishaAddress = async () => {
            if (valid.value.data.error && valid.value.data.errorFields.includes("shinseishaPostcode")) {
                return;
            }
            //ハイフン対応
            if (shomei.shinseishaPostcode.length === 7) {
                shomei.shinseishaPostcode = shomei.shinseishaPostcode.substring(0, 3) + "-" + shomei.shinseishaPostcode.substring(3);
            }
            try {
                if (/^\d{3}-\d{4}$/.test(shomei.shinseishaPostcode)) {
                    shomei.shinseishaAdr1 = await createAddress(shomei.shinseishaPostcode);
                }
            }
            catch (ex) {
                // 該当する住所が存在しない場合は404が投げられる
                if (ex?.status !== 404) {
                    log.error("findShinseishaAddress", ex);
                }
            }
        };
        // 申請者ハイフン付きの電話番号に整形
        let formatShinseishaTel = () => {
            if (myPageStore.personal.userKb === userType.hojin) {
                return;
            }
            if (valid.value.data.error && valid.value.data.errorFields.includes("shinseishaTel")) {
                return;
            }
            //ハイフン対応
            if (myPageStore.personal.userKb === userType.Kojin && shomei.shinseishaTel.length !== 13 && shomei.shinseishaTel.length !== 0 && shomei.usedByShiyousha) {
                let temp = shomei.shinseishaTel.replace("-", "");
                shomei.shinseishaTel = temp.substring(0, 3) + "-" + temp.substring(3, 7) + "-" + temp.substring(7);
            }
        };
        /**
         * 日付 ViewModel値更新 イベントハンドラ<br />
         * コンポーネントの呼び出し元で定義された値更新イベントを起動します。
         * @param value 入力値
         */
        const onModelUpdate = (value) => {
            if (isComposing) {
                composingInputted = value;
                return;
            }
            // クリア等でnullが設定されている場合を考慮
            const formatTarget = value ?? "";
            // 全角数字を半角数字に変換
            const half = formatTarget.replace(/[０-９]/g, (s) => {
                return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
            });
            // 数字のみ抽出
            const digits = half.replace(/\D/g, "").slice(0, dateDigits);
            // YYYY/MM/DD形式に変換
            let formatted = "";
            if (digits.length >= 7) {
                formatted = digits.slice(0, 4) + "/" + digits.slice(4, 6) + "/" + digits.slice(6, 8);
            }
            else if (digits.length >= 5) {
                formatted = digits.slice(0, 4) + "/" + digits.slice(4, 6);
            }
            else {
                formatted = digits.slice(0, 4);
            }
            props.updateValue("");
            props.updateValue(formatted);
        };
        /**
         * 日付 値変更イベントハンドラ
         * ・年月選択の場合、操作内容に応じて下記の動作を行います。
         *   ・月選択画面から年選択画面に遷移していた場合、月選択画面を再表示し、イベントを再設定します。
         *   ・月選択画面から年移動矢印をクリックしていた場合、画面上部のイベントが削除されるため、イベントを再設定します。
         *   ・それ以外の場合、月選択画面で月を選択していたものとして、カレンダーを閉じます。
         * ・日付選択の場合、カレンダーを閉じます。
         * @param {string} value - 入力された値
         */
        const onDataInput = (which, value) => {
            if (props.isPickMonth) {
                isNotClickDay.value = false;
                if (isMonthToYear.value) {
                    isMonthToYear.value = false;
                    if (which === "start") {
                        qStartDateProxy.value?.setView("Months");
                    }
                    else {
                        qEndDateProxy.value?.setView("Months");
                    }
                    runEventWithDelay(setupEvents, 128);
                }
                else if (isMonthMoveYear.value) {
                    isMonthMoveYear.value = false;
                    runEventWithDelay(setupEvents, 128);
                }
                else {
                    clearEvents();
                    if (value !== null) {
                        if (which === "start") {
                            shomei.rentStartDate = value + "/01";
                            startDateInput.value = value + "/01";
                        }
                        else {
                            const [year, month] = value.split("/");
                            const lastDay = new Date(Number(year), Number(month), 0).getDate();
                            const date = `${year}/${String(month).padStart(2, "0")}/${lastDay}`;
                            shomei.rentEndDate = date;
                            endDateInput.value = date;
                        }
                    }
                    if (which === "start") {
                        qStartPopupProxy.value?.hide();
                    }
                    else {
                        qEndPopupProxy.value?.hide();
                    }
                }
            }
            else {
                if (isNotClickDay.value) {
                    runEventWithDelay(setupEvents, 128);
                }
                else {
                    clearEvents();
                    if (value !== null) {
                        if (which === "start") {
                            shomei.rentStartDate = value;
                            startDateInput.value = value;
                        }
                        else {
                            shomei.rentEndDate = value;
                            endDateInput.value = value;
                        }
                    }
                    if (which === "start") {
                        qStartPopupProxy.value?.hide();
                    }
                    else {
                        qEndPopupProxy.value?.hide();
                    }
                }
                isNotClickDay.value = false;
                isMonthMoveYear.value = false;
            }
        };
        // ファイルダイアログを表示
        const openFile = () => {
            fileInput.value?.pickFiles();
        };
        // ファイル削除
        const deleteFile = () => {
            shomei.file = null;
        };
        /**
         * カレンダー 年選択画面 年クリック イベントハンドラ<br />
         * カレンダー操作イベントハンドラをクリアします。
         */
        const onClickYears = () => {
            isMonthToYear.value = false;
            isMonthMoveYear.value = false;
            isNotClickDay.value = true;
            clearEvents();
        };
        /**
         * カレンダー 月選択画面 月クリック イベントハンドラ<br />
         * カレンダー操作イベントハンドラをクリアします。
         */
        const onClickMonths = () => {
            isMonthToYear.value = false;
            isMonthMoveYear.value = false;
            isNotClickDay.value = true;
            clearEvents();
        };
        /**
         * カレンダー 月選択画面 年クリック イベントハンドラ<br />
         * カレンダー操作イベントハンドラをクリアします。
         */
        const onClickMonthsToYears = () => {
            isMonthToYear.value = true;
            isMonthMoveYear.value = false;
            isNotClickDay.value = true;
            clearEvents();
        };
        /**
         * カレンダー 月選択画面 年移動矢印クリック イベントハンドラ<br />
         * カレンダー操作イベントハンドラをクリアします。
         */
        const onClickMonthsMoveYears = () => {
            isMonthToYear.value = false;
            isMonthMoveYear.value = true;
            isNotClickDay.value = true;
            clearEvents();
        };
        /**
         * 現在のカレンダーコンポーネントから、定義したカレンダー操作イベントハンドラを全てクリアします。
         */
        const clearEvents = () => {
            const qStartDateEl = qStartDateProxy.value?.$el;
            const qEndDateEl = qEndDateProxy.value?.$el;
            if (!qStartDateEl && !qEndDateEl) {
                return;
            }
            if (qStartDateEl) {
                // 年選択画面で年選択
                qStartDateEl.querySelectorAll(".q-date__years-item .q-btn__content").forEach((el) => {
                    el.removeEventListener("click", onClickYears);
                });
                // 月選択画面で月選択
                qStartDateEl.querySelectorAll(".q-date__months-item .q-btn__content").forEach((el) => {
                    el.removeEventListener("click", onClickMonths);
                });
                // 月選択画面で上部の年選択
                qStartDateEl.querySelectorAll(".q-btn--no-uppercase").forEach((el) => {
                    el.removeEventListener("click", onClickMonthsToYears);
                });
                // 月選択画面で上部の年移動矢印選択
                qStartDateEl.querySelectorAll(".q-date__arrow .q-btn__content").forEach((el) => {
                    el.removeEventListener("click", onClickMonthsMoveYears);
                });
            }
            else {
                // 年選択画面で年選択
                qEndDateEl.querySelectorAll(".q-date__years-item .q-btn__content").forEach((el) => {
                    el.removeEventListener("click", onClickYears);
                });
                // 月選択画面で月選択
                qEndDateEl.querySelectorAll(".q-date__months-item .q-btn__content").forEach((el) => {
                    el.removeEventListener("click", onClickMonths);
                });
                // 月選択画面で上部の年選択
                qEndDateEl.querySelectorAll(".q-btn--no-uppercase").forEach((el) => {
                    el.removeEventListener("click", onClickMonthsToYears);
                });
                // 月選択画面で上部の年移動矢印選択
                qEndDateEl.querySelectorAll(".q-date__arrow .q-btn__content").forEach((el) => {
                    el.removeEventListener("click", onClickMonthsMoveYears);
                });
            }
        };
        /**
         * 現在のカレンダーコンポーネントに対し、カレンダー操作イベントハンドラを設定します。
         */
        const setupEvents = () => {
            const qStartDateEl = qStartDateProxy.value?.$el;
            const qEndDateEl = qEndDateProxy.value?.$el;
            if (!qStartDateEl && !qEndDateEl) {
                return;
            }
            if (qStartDateEl) {
                // 年選択画面で年選択
                qStartDateEl.querySelectorAll(".q-date__years-item .q-btn__content").forEach((el) => {
                    el.addEventListener("click", onClickYears);
                });
                // 月選択画面で月選択
                qStartDateEl.querySelectorAll(".q-date__months-item .q-btn__content").forEach((el) => {
                    el.addEventListener("click", onClickMonths);
                });
                // 月選択画面で上部の年選択
                qStartDateEl.querySelectorAll(".q-btn--no-uppercase").forEach((el) => {
                    el.addEventListener("click", onClickMonthsToYears);
                });
                // 月選択画面で上部の年移動矢印選択
                qStartDateEl.querySelectorAll(".q-date__arrow .q-btn__content").forEach((el) => {
                    el.addEventListener("click", onClickMonthsMoveYears);
                });
            }
            else {
                // 年選択画面で年選択
                qEndDateEl.querySelectorAll(".q-date__years-item .q-btn__content").forEach((el) => {
                    el.addEventListener("click", onClickYears);
                });
                // 月選択画面で月選択
                qEndDateEl.querySelectorAll(".q-date__months-item .q-btn__content").forEach((el) => {
                    el.addEventListener("click", onClickMonths);
                });
                // 月選択画面で上部の年選択
                qEndDateEl.querySelectorAll(".q-btn--no-uppercase").forEach((el) => {
                    el.addEventListener("click", onClickMonthsToYears);
                });
                // 月選択画面で上部の年移動矢印選択
                qEndDateEl.querySelectorAll(".q-date__arrow .q-btn__content").forEach((el) => {
                    el.addEventListener("click", onClickMonthsMoveYears);
                });
            }
        };
        /**
         * 指定した処理を、指定した時間待機後に実行します。
         * @param event 処理メソッド
         * @param delayMs 待機時間(ミリ秒)
         */
        const runEventWithDelay = async (event, delayMs) => {
            const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
            await delay(delayMs);
            event();
        };
        /**
         * q-dateコンポーネント 変更検知 ハンドラ
         * イベントハンドラの再設定を実施します。
         * カレンダー初期表示時の動作を想定しています。
         */
        watch(qStartDateProxy, qData => {
            if (isMonthToYear.value) {
                return;
            }
            clearEvents();
            setupEvents();
        });
        /**
         * q-dateコンポーネント 変更検知 ハンドラ
         * イベントハンドラの再設定を実施します。
         * カレンダー初期表示時の動作を想定しています。
         */
        watch(qEndDateProxy, qData => {
            if (isMonthToYear.value) {
                return;
            }
            clearEvents();
            setupEvents();
        });
        /**
         * IME入力開始イベントハンドラ
         * IME入力中フラグを立てます。
         */
        function onCompositionStart() {
            isComposing = true;
        }
        /**
         * IME入力開始イベントハンドラ
         * IME入力中フラグを倒し、IME入力中のテキストを元に値更新イベントを起動します。
         */
        function onCompositionEnd() {
            isComposing = false;
            nextTick(() => {
                onModelUpdate(composingInputted);
                composingInputted = "";
            });
        }
        /**
         * コンポーネント マウント時処理
         * q-inputにIME入力イベントハンドラを設定します。
         */
        onMounted(() => {
            const el = qInputProxy.value?.getNativeElement?.();
            el?.addEventListener("compositionstart", onCompositionStart);
            el?.addEventListener("compositionend", onCompositionEnd);
        });
        /**
         * コンポーネント アンマウント時処理
         * q-inputに設定したIME入力イベントハンドラを解除します。
         */
        onBeforeUnmount(() => {
            const el = qInputProxy.value?.getNativeElement?.();
            el?.removeEventListener("compositionstart", onCompositionStart);
            el?.removeEventListener("compositionend", onCompositionEnd);
        });
        // eslint-disable-next-line complexity
        const submit = async () => {
            const allowedFields = new Set(["shinseishaName", "shinseishaPostcode", "shinseishaAdr1", "shinseishaAdr2", "shinseishaAdr3", "shinseishaTel"]);
            if (valid.value.error) {
                const errorFields = valid.value.errorFields;
                // 車庫証明の場合
                if (shomei.isShakoShomei) {
                    for (const field of errorFields) {
                        if (field !== "rentStartDate" && field !== "rentEndDate" && (shomei.usedByShiyousha || !allowedFields.has(field)) && (shomei.shosikiSiteiKb === 1 || field !== "file")) {
                            MsgBox.error("入力に不備があります。<br/>確認してください。");
                            return;
                        }
                    }
                }
                else if (shomei.isRentShomei) {
                    for (const field of errorFields) {
                        if (field !== "shakoChushamei" &&
                            field !== "shakoKukaku" &&
                            field !== "shinseishaName" &&
                            field !== "shinseishaPostcode" &&
                            field !== "shinseishaAdr1" &&
                            field !== "shinseishaAdr2" &&
                            field !== "shinseishaAdr3" &&
                            field !== "shinseishaTel" &&
                            (shomei.shosikiSiteiKb === 1 || field !== "file")) {
                            MsgBox.error("入力に不備があります。<br/>確認してください。");
                            return;
                        }
                    }
                }
                else {
                    for (const field of errorFields) {
                        if (field !== "rentStartDate" &&
                            field !== "rentEndDate" &&
                            field !== "shakoChushamei" &&
                            field !== "shakoKukaku" &&
                            field !== "shinseishaName" &&
                            field !== "shinseishaPostcode" &&
                            field !== "shinseishaAdr1" &&
                            field !== "shinseishaAdr2" &&
                            field !== "shinseishaAdr3" &&
                            field !== "shinseishaTel" &&
                            (shomei.shosikiSiteiKb === 1 || field !== "file")) {
                            MsgBox.error("入力に不備があります。<br/>確認してください。");
                            return;
                        }
                    }
                }
            }
            //ハイフン対応
            if (shomei.postcode.length === 7) {
                shomei.postcode = shomei.postcode.substring(0, 3) + "-" + shomei.postcode.substring(3);
            }
            if (myPageStore.personal.userKb === userType.Kojin && shomei.tel.length !== 13) {
                let temp = shomei.tel.replace("-", "");
                shomei.tel = temp.substring(0, 3) + "-" + temp.substring(3, 7) + "-" + temp.substring(7);
            }
            if (myPageStore.personal.userKb === userType.Kojin && shomei.shinseishaTel.length !== 13 && shomei.usedByShiyousha) {
                let temp = shomei.shinseishaTel.replace("-", "");
                shomei.shinseishaTel = temp.substring(0, 3) + "-" + temp.substring(3, 7) + "-" + temp.substring(7);
            }
            let addressAll = "";
            addressAll += "〒" + shomei.postcode;
            addressAll += "\r\n" + shomei.adr1;
            if (shomei.adr2 !== "") {
                addressAll += "\r\n" + shomei.adr2;
            }
            if (shomei.adr3 !== "") {
                addressAll += "\r\n" + shomei.adr3;
            }
            shomei.addressAll = addressAll;
            shomei.tesuryo = shomei.selectedShomeiKbn?.option1 ?? "";
            shomei.fromEntry = true;
            store.setData(shomei);
            await router.push({ name: "shomei-shinsei-entry-check" });
        };
        if (!store.shomeiEntry.fromEntry) {
            await load();
            // 契約情報表示用
            shomei.shinseiYmd = new Date();
            shomei.soufuName = personal.hasAddress ? personal.mailName : personal.keiyakusha;
            shomei.postcode = personal.hasAddress ? personal.mailPostcode : myPageStore.selectedNyukyokeiyaku?.postCode ?? "";
            shomei.adr1 = personal.hasAddress ? personal.mailAdr1 : myPageStore.selectedNyukyokeiyaku?.adr1 ?? "";
            shomei.adr2 = personal.hasAddress ? personal.mailAdr2 : myPageStore.selectedNyukyokeiyaku?.adr2 ?? "";
            shomei.adr3 = personal.hasAddress
                ? personal.mailAdr3
                : `${myPageStore.selectedNyukyokeiyaku?.bukkenName ?? ""}${myPageStore.selectedNyukyokeiyaku?.roomNumber ? " " + myPageStore.selectedNyukyokeiyaku?.roomNumber : ""}`;
            shomei.email = personal.email;
        }
        extend(true, shomei, store.shomeiEntry);
        initView();
        return {
            ...toRefs(store),
            ...useLoad(shomeiNo),
            myPageStore,
            userType,
            PageID,
            ShomeiSb,
            props,
            isMultipleKeiyaku,
            pageName,
            addressAll,
            loading,
            load,
            personal,
            shomei,
            valid,
            html,
            shosikiSiteiHtml,
            qInputProxy,
            startDateInput,
            endDateInput,
            qStartPopupProxy,
            qEndPopupProxy,
            qEndDateProxy,
            qStartDateProxy,
            defaultViewType,
            dateMaskFormat,
            options,
            findAddress,
            formatTel,
            onChanged,
            findShinseishaAddress,
            onModelUpdate,
            onDataInput,
            date,
            submit,
            onCompositionStart,
            onCompositionEnd,
            clearFields,
            formatShinseishaTel,
            openFile,
            fileInput,
            deleteFile
        };
    }
});
